import React, { useContext, useEffect, useState } from 'react'
import { Trans, t, Plural } from '@lingui/macro'
import { Dayjs } from 'dayjs'

import IconArrowRight from '../../../../components/common/icon/IconArrowRight'
import { ButtonPrimary } from 'front-end/src/components'
import Label from '../../../../components/common/form/label/Label'
import NewActionHeader from '../new-action-header/NewActionHeader'
import uniqueId from '../../../../components/utils/uniqueId'
import { linkBuilder } from '../../../../../../shared/linkBuilder'
import { navigateTo } from '../../../../app/util/appHistory'
import { notification } from '../../../../components/common/notifications/Notifications'
import { ManageCampaignContext } from '../../ManageCampaignPage'
import { CampaignActionScheduleStepComponent } from '../campaign-action-schedule-step-component/CampaignActionScheduleStepComponent'
import {
  CampaignMessageActionInput,
  CampaignMessageActionModelAsEditor,
  CampaignMessageTypeEnum,
  CampaignScheduleEnum
} from 'front-end/src/graphql/private/PrivateSchemaTypes'

import Select from '../../../../components/common/select/Select'
import Textarea from '../../../../components/common/form/textarea/Textarea'
import { searchLinksInText } from '../../../../../../back-end/src/util/strings'
import { AccessibilityLabel } from '../../../../components/common/AccessibilityLabel'
import { CampaignActionEditingSteps, campaignMessageTypes } from '../../common'
import CustomQuill from '../../../../components/common/quill/CustomQuill'
import {
  useCreateCampaignMessageMutation,
  useOrganisationSubdomainQuery
} from './createCampaignMessageMutation.codegen'
import { usePatchCampaignMessageMutation } from './patchCampaignMessageMutation.codegen'
import { TemplatingInfoBar } from '../TemplatingInfoBar'
import { useFeatureFlags } from 'front-end/src/app/hooks/useFeatureFlags'
import dayjs from 'shared/dayjs'

import './CampaignMessageComponent.less'
import { CampaignActionTypeEnum } from 'shared/enums/campaigns'
import { getUTCDateFromTimeZoneDate } from 'front-end/src/pages/manage-events/event-edit/tabs/event-detail/eventDetailUtils'

export interface ICampaignMessageComponentProps {
  message?: CampaignMessageActionModelAsEditor
}

export interface ICampaignMessageComponentState {
  messageBody: string
  automatic_time_of_day: boolean
  respect_business_hours: boolean
  step: string
  timezone: string | null
  scheduleType: CampaignScheduleEnum
  relativeSchedule: {
    day: number | null
    time: string | null
  }
  fixedSchedule: { date: Dayjs | null }
  isSaving: boolean
  saveError: Error | null
  isEditing: boolean
  type: CampaignMessageTypeEnum
}

const MAX_SMS_CHARACTERS = 160

const { PREVIEW, ...NoPreviewCampaignActionEditingSteps } =
  CampaignActionEditingSteps

export default (props: ICampaignMessageComponentProps) => {
  const { campaign } = useContext(ManageCampaignContext)
  const [createCampaignMessage] = useCreateCampaignMessageMutation()
  const [patchCampaignMessage] = usePatchCampaignMessageMutation()
  const { message } = props

  const [state, setState] = useState<ICampaignMessageComponentState>({
    messageBody: '',
    step: CampaignActionEditingSteps.CREATE,
    timezone: '',
    scheduleType: campaign.schedule_type as CampaignScheduleEnum,
    automatic_time_of_day: message?.automatic_time_of_day ?? false,
    respect_business_hours: message?.respect_business_hours ?? false,
    relativeSchedule: {
      day: 1,
      time: ''
    },
    fixedSchedule: {
      date: null
    },
    isSaving: false,
    saveError: null,
    isEditing: false,
    type: CampaignMessageTypeEnum.SMS
  })

  useEffect(() => {
    if (props.message) {
      const { message } = props
      const timezone = message.timezone ? message.timezone : dayjs.tz.guess()

      onStateUpdate({
        // @ts-expect-error 9/11/2020
        messageBody: message.text,
        timezone: timezone,
        scheduleType: campaign.schedule_type as CampaignScheduleEnum,
        relativeSchedule: {
          day: message.send_at_day ?? 1,
          time: message.send_at_time || '10:00:00'
        },
        fixedSchedule: {
          date: dayjs(message.fixed_send_time as string).tz(timezone)
        },
        isEditing: true,
        // @ts-expect-error 9/11/2020
        type: message.type
      })
    }
  }, [props])

  const onStateUpdate = (
    updatedState: Partial<ICampaignMessageComponentState>
  ) => {
    setState({ ...state, ...updatedState })
  }

  const create = async (input: CampaignMessageActionInput) => {
    try {
      onStateUpdate({ isSaving: true })

      const { data } = await createCampaignMessage({
        variables: {
          id: campaign.id,
          input
        }
      })
      return data?.admin?.campaign?.createMessage
    } catch (err) {
      onStateUpdate({ saveError: err })
      return null
    }
  }

  const update = async (input: CampaignMessageActionInput) => {
    if (campaign.id && props.message) {
      try {
        onStateUpdate({ isSaving: true, saveError: null })
        const { data } = await patchCampaignMessage({
          variables: {
            id: props.message.id,
            campaignId: campaign.id,
            input
          }
        })
        return data?.admin?.campaign?.messageAction?.patch
      } catch (err) {
        onStateUpdate({ saveError: err, isSaving: false })
      }
    }
  }

  const onSave = async () => {
    const data: CampaignMessageActionInput = {
      text: state.messageBody,
      type: state.type,
      fixed_send_time: null,
      timezone: null,
      send_at_day: null,
      send_at_time: null,
      automatic_time_of_day: state.automatic_time_of_day,
      respect_business_hours: state.respect_business_hours
    }

    if (state.scheduleType === CampaignScheduleEnum.RELATIVE) {
      data.send_at_day = state.relativeSchedule.day
      data.send_at_time = state.relativeSchedule.time
    } else if (state.scheduleType === CampaignScheduleEnum.FIXED) {
      const timezone = state.timezone || dayjs.tz.guess()
      data.timezone = timezone
      data.fixed_send_time = getUTCDateFromTimeZoneDate({
        date: state.fixedSchedule.date,
        timezone
      })
    }

    const isCreating = state.isEditing === false
    const updatedMessage = isCreating ? await create(data) : await update(data)

    if (!updatedMessage) {
      onStateUpdate({ isSaving: false })
      notification.error(
        isCreating ? t`Error creating message` : t`Error updating message`
      )
      return
    }

    onStateUpdate({ isSaving: false })
    notification.info(
      isCreating
        ? t`Campaign's message has been created`
        : t`Campaign's message has been updated`
    )

    navigateTo(linkBuilder.campaign(campaign.id))
  }

  const { step } = state

  return (
    <div className="CampaignEmailComponent">
      {step === CampaignActionEditingSteps.CREATE && (
        <StepCreate {...state} onStateUpdate={onStateUpdate} />
      )}
      {step === CampaignActionEditingSteps.SCHEDULE && (
        <CampaignActionScheduleStepComponent
          {...state}
          onStateUpdate={onStateUpdate}
          onSave={onSave}
          actionType={CampaignActionTypeEnum.MESSAGE}
          // @ts-expect-error 9/11/2020
          steps={NoPreviewCampaignActionEditingSteps}
        />
      )}
    </div>
  )
}

const StepCreate = (
  props: ICampaignMessageComponentState & {
    onStateUpdate: (
      updatedState: Partial<ICampaignMessageComponentState>
    ) => void
  }
) => {
  const { campaign } = useContext(ManageCampaignContext)
  const { data } = useOrganisationSubdomainQuery()
  const subdomainFullUrl = data?.admin?.organisation?.subdomain?.fullUrl

  const { featureFlags } = useFeatureFlags()

  let availableTypes = campaignMessageTypes

  if (!featureFlags?.Fourth) {
    availableTypes = availableTypes.filter(
      (availableType) => availableType.value !== CampaignMessageTypeEnum.FOURTH
    )
  }

  if (!featureFlags?.Slack) {
    availableTypes = availableTypes.filter(
      (availableType) => availableType.value !== CampaignMessageTypeEnum.SLACK
    )
  }

  if (!featureFlags?.sms) {
    availableTypes = availableTypes.filter(
      (availableType) => availableType.value !== CampaignMessageTypeEnum.SMS
    )
  }

  if (!featureFlags?.msteams) {
    availableTypes = availableTypes.filter(
      (availableType) => availableType.value !== CampaignMessageTypeEnum.MSTEAMS
    )
  }
  const messageBodyId: string = uniqueId()
  const scheduleEnabled = (): boolean => {
    if (props.type === CampaignMessageTypeEnum.SMS) {
      return (
        !!props.messageBody &&
        getMessageLength(
          props.messageBody,
          subdomainFullUrl as string,
          campaign.track_links
        ) <= MAX_SMS_CHARACTERS
      )
    }
    return !!props.messageBody
  }

  const getAvailableIntegrationIndex = (type: string): number => {
    return availableTypes.findIndex((availableType) => {
      return availableType.value === type
    })
  }

  const renderScheduleDisabledReason = () => {
    if (
      props.type === CampaignMessageTypeEnum.SMS &&
      getMessageLength(
        props.messageBody,
        subdomainFullUrl as string,
        campaign.track_links
      ) > MAX_SMS_CHARACTERS
    ) {
      const linksCount = searchLinksInText(props.messageBody).length
      return (
        <span className="CampaignMessageComponent__error-reason text-small">
          <Trans>Your message is too long.</Trans>
          {campaign.track_links && hasMessageLinks(props.messageBody) && (
            <>
              <br />
              <Plural
                value={linksCount}
                _0={null}
                one="You have # tracked link that lengthens message."
                other="You have # tracked links that lengthens message."
              />
            </>
          )}
        </span>
      )
    }
  }

  return (
    <div className="CampaignMessageComponent CampaignEmailComponent__create">
      <NewActionHeader
        title={t`Send message`}
        steps={NoPreviewCampaignActionEditingSteps}
        currentStep={CampaignActionEditingSteps.CREATE}
        nextButton={
          <ButtonPrimary
            onClick={() => {
              props.onStateUpdate({
                step: CampaignActionEditingSteps.SCHEDULE
              })
            }}
            disabled={!scheduleEnabled()}
            rightIcon={<IconArrowRight />}
          >
            <Trans>Schedule</Trans>
          </ButtonPrimary>
        }
      />

      <section>
        <TemplatingInfoBar />

        <AccessibilityLabel text={t`Role Select`}>
          <Select
            options={availableTypes}
            value={availableTypes[getAvailableIntegrationIndex(props.type)]}
            onChange={({ value }: any) => {
              props.onStateUpdate({ type: value })
            }}
            className="roleSelect"
          />
        </AccessibilityLabel>
      </section>

      <section>
        <Label htmlFor={messageBodyId}>
          <Trans>Message body</Trans>
        </Label>
        {props.type === CampaignMessageTypeEnum.FOURTH && (
          <Textarea
            title={t`Message`}
            placeholder={t`Add a message...`}
            value={props.messageBody}
            onChange={(value: string): void =>
              props.onStateUpdate({ messageBody: value })
            }
          />
        )}
        {props.type === CampaignMessageTypeEnum.SLACK && (
          <Textarea
            title={t`Message`}
            placeholder={t`Add a message...`}
            value={props.messageBody}
            onChange={(value: string): void =>
              props.onStateUpdate({ messageBody: value })
            }
          />
        )}
        {props.type === CampaignMessageTypeEnum.MSTEAMS && (
          <CustomQuill
            value={props.messageBody}
            onChange={(value: string): void =>
              props.onStateUpdate({ messageBody: value })
            }
            toolbarItems={[
              ['bold', 'italic', 'underline'],
              [{ list: 'ordered' }, { list: 'bullet' }],
              ['link'],
              [{ header: 1 }, { header: 2 }],
              ['clean']
            ]}
          />
        )}
        {props.type === CampaignMessageTypeEnum.SMS && (
          <div>
            <Textarea
              title={t`Message`}
              placeholder={t`Add a message...`}
              value={props.messageBody}
              onChange={(value: string): void => {
                props.onStateUpdate({ messageBody: value })
              }}
            />
            <label className="Label">
              {renderMessageLength(
                props.messageBody,
                subdomainFullUrl as string,
                campaign.track_links
              )}
            </label>
            {campaign.track_links && hasMessageLinks(props.messageBody) && (
              <div>
                <label className="Label Label--narrow">
                  <Trans>Tracked links</Trans>:{' '}
                  {searchLinksInText(props.messageBody).length}
                  &nbsp;
                </label>
              </div>
            )}
          </div>
        )}
      </section>

      <section className="CampaignMessageComponent__footer">
        {renderScheduleDisabledReason()}
        <ButtonPrimary
          disabled={!scheduleEnabled()}
          onClick={() => {
            props.onStateUpdate({
              step: CampaignActionEditingSteps.SCHEDULE
            })
          }}
        >
          <Trans>Schedule</Trans> <IconArrowRight />
        </ButtonPrimary>
      </section>
    </div>
  )
}

const hasMessageLinks = (messageBody) => {
  return searchLinksInText(messageBody).length > 0
}

const getMessageLength = (
  messageBody: string,
  subdomainFullUrl: string,
  trackLinksActive: boolean
): number => {
  if (!trackLinksActive) {
    return messageBody.length
  }
  const trackedLinks = searchLinksInText(messageBody)
  if (!trackedLinks) {
    return messageBody.length
  }

  const EXAMPLE_HASH = 'a5c80d6ab506'

  const replacedLink = `${subdomainFullUrl}${linkBuilder
    .trackedLink(EXAMPLE_HASH)
    .substring(1)}`

  return (
    messageBody.length -
    trackedLinks.join('').length +
    trackedLinks.length * replacedLink.length
  )
}

const renderMessageLength = (
  messageBody: string,
  subdomainFullUrl: string,
  trackLinksActive: boolean
) => {
  const lengthWithReplacedLinks = getMessageLength(
    messageBody,
    subdomainFullUrl,
    trackLinksActive
  )

  if (lengthWithReplacedLinks > MAX_SMS_CHARACTERS) {
    return (
      <>
        <Trans>Message length</Trans>:{' '}
        <span style={{ color: 'red' }}>{lengthWithReplacedLinks}</span>
      </>
    )
  }

  return (
    <>
      <Trans>Message length</Trans>: {lengthWithReplacedLinks}
    </>
  )
}
